import { create } from 'xmlbuilder2'
import { EthercatDomain } from '/@/shared/entities/EthercatDomain'
import { EthercatDevice } from '/@/shared/entities/EthercatDevice'
import { PDO } from '/@/shared/entities/PDO'
import { Dir } from '/@/shared/entities/Dir'
import { SDO } from '/@/shared/entities/SDO'
import { extractIfExists } from '../utils'
import {
  formatIndex,
  createLinkElements,
  createEntryElements,
  createSDOElement,
  createMailboxElements,
  addVisiblePoperty,
  addIncludedProperty,
} from './helpers'
import { XMLBuilder } from 'xmlbuilder2/lib/interfaces'
import { DistributedClockSetting, DistributedClockSettingMode } from '../entities/DistributedClock'
class Configuration {
  private root: XMLBuilder
  private name = 'Motorcortex'
  private attributes: { [key: string]: any } = {
    Version: '2.0',
  }
  private readonly pretty: boolean

  constructor(data: EthercatDomain[], pretty: boolean = true) {
    this.pretty = pretty
    this.root = create().ele(this.name)

    this.setRootAttributes()
    const fieldbus = this.addFieldbus()

    data.forEach((domain) => {
      const element = fieldbus.ele('Domain', {
        Group: '',
        Name: domain.displayName,
        Simulation: domain.isSimulated,
        ...(domain.frequencyDivider > 1 ? { FrequencyDivider: Math.trunc(domain.frequencyDivider) } : {}),
        ...addIncludedProperty(domain),
      })

      this.addDevices(domain, element)
    })
  }

  private addDevices(domain: EthercatDomain, domainEl: XMLBuilder) {
    domain.devices.forEach((device) => {
      const deviceEl = domainEl.ele('Device', {
        Alias: device.id,
        Name: device.name,
        AllowOverlappingPdos: device.allowOverlappingPdos,
        ...addIncludedProperty(device),
      })

      this.configureDevice(device, deviceEl)
    })
  }

  private configureDevice(device: EthercatDevice, deviceEl: XMLBuilder) {
    createMailboxElements(device, deviceEl.ele('Mailbox'))

    createLinkElements(deviceEl, device, 'LinkSimulation')

    this.configureDC(
      device,
      deviceEl.ele('Dc', {
        ...addIncludedProperty(device.distributedClock),
      }),
    )

    deviceEl
      .ele('Type', {
        VendorId: device.vendorId,
        ProductCode: device.type.productCode,
        RevisionNo: device.type.revisionNo,
      })
      .txt(device.type.name)

    device.syncManagers.forEach((syncManager) => {
      deviceEl
        .ele('Sm', {
          Enable: syncManager.enable,
          StartAddress: syncManager.startAddress,
          ControlByte: syncManager.controlByte,
          DefaultSize: syncManager.defaultSize,
        })
        .txt(syncManager?.name)
    })

    this.addPDOs(device.pdos, deviceEl)
    this.addSDOs(device.sdos, deviceEl)
  }

  private configureDC(device: EthercatDevice, dcEl: XMLBuilder) {
    dcEl.ele('AssignActivate').txt(device.distributedClock.assignActivate.toString())

    this.configureDCSetting('CycleTimeSync0', device.distributedClock.cycleTS0, dcEl)
    this.configureDCSetting('CycleTimeSync1', device.distributedClock.cycleTS1, dcEl)
    this.configureDCSetting('ShiftTimeSync0', device.distributedClock.shiftTS0, dcEl)
    this.configureDCSetting('ShiftTimeSync1', device.distributedClock.shiftTS1, dcEl)
  }

  private configureDCSetting(name: string, setting: DistributedClockSetting, dcEl: XMLBuilder) {
    const factor =
      setting.mode === DistributedClockSettingMode.CYCLE //
        ? extractIfExists(setting, 'factor')
        : {}

    dcEl
      .ele(name, {
        ...factor,
        UiHidden: !setting.isIncluded,
      })
      .txt(setting.value.toString())
  }

  private addSDOs(sdos: SDO[], deviceEl: XMLBuilder) {
    const rxSdos = sdos.filter((sdo) => sdo.dir === Dir.RX)
    const txSdos = sdos.filter((sdo) => sdo.dir === Dir.TX)

    this.createSDOs(Dir.sdoTag(Dir.RX), deviceEl, rxSdos)
    this.createSDOs(Dir.sdoTag(Dir.TX), deviceEl, txSdos)
  }

  private createSDOs(name: string, deviceEl: XMLBuilder, sdos: SDO[]) {
    sdos.forEach((sdo) => createSDOElement(deviceEl, name, sdo))
  }

  private addPDOs(pdos: PDO[], deviceEl: XMLBuilder) {
    const rxPdos = pdos.filter((pdo) => pdo.dir === Dir.RX)
    const txPdos = pdos.filter((pdo) => pdo.dir === Dir.TX)

    this.createPDOs('RxPdo', deviceEl, rxPdos)
    this.createPDOs('TxPdo', deviceEl, txPdos)
  }

  private createPDOs(name: string, deviceEl: XMLBuilder, pdos: PDO[]) {
    const createEl = (name: string, pdo: PDO) => {
      return deviceEl.ele(name, {
        Sm: pdo.sm,
        Fixed: pdo.fixed,
        Mandatory: pdo.mandatory,
        ...extractIfExists(pdo, 'name'),
        ...extractIfExists(pdo, 'group'),
        ...addVisiblePoperty(pdo),
        ...addIncludedProperty(pdo),
      })
    }

    pdos.forEach((pdo) => {
      const pdoEl = createEl(name, pdo)
      pdoEl.ele('Index').txt(formatIndex(pdo.index))

      createLinkElements(pdoEl, pdo)
      createEntryElements(pdoEl, pdo)
    })
  }

  private addFieldbus() {
    return this.root.ele('FieldBusList').ele('FieldBus', {
      Type: 'EtherCAT',
      Device: 0,
      Name: 'Ethercat',
    })
  }

  private setRootAttributes() {
    Object.keys(this.attributes).forEach((key) => this.root.att(key, this.attributes[key]))
  }

  public toXML() {
    return this.root.end({ prettyPrint: this.pretty })
  }
}

export default Configuration
